import React, { useEffect } from 'react'
import { Body } from '@cjdev-internal/visual-stack-x/Panel'
import {
  CJForm,
  useCJForm,
  CJField,
  TextInput,
  SelectInput
} from '@cjdev-internal/visual-stack-x/components/CJForm'
import { Button } from '@cjdev-internal/visual-stack-x/Button'
import { Notification } from '@cjdev-internal/visual-stack-x/Notification'
import { Text } from '@cjdev-internal/visual-stack-x/Text'
import { AlternatePayee, AlternatePayeeFormProps, LastModificationDateProps } from '../../../types'
import * as R from 'ramda'

const AlternatePayeeForm = (
  { formStrategy, fetchingStrategy, defaultValues, bankCountryOptions, paymentCurrencyOptions, accountTypeOptions, intl, handleSubmit, validator }: AlternatePayeeFormProps) => {

  const formControl = useCJForm<AlternatePayee>({
    mode: 'onChange',
    defaultValues
  })

  const onFormSubmit = formControl.handleSubmit(handleSubmit);
  const { isFetching, FetchingIndicator } = fetchingStrategy;

  const getButtonLabel = (labelKey: string): string => {
    const labelMap: Record<string, string> = {
      view: intl.alternatePayeeForm.viewButton,
      edit: intl.alternatePayeeForm.editButton,
      save: intl.alternatePayeeForm.saveButton,
      cancel: intl.alternatePayeeForm.cancelButton
    };
    return labelMap[labelKey] || labelKey;
  };

  const accountNumberClone = formStrategy.showBankingNumbers && R.clone(defaultValues.accountNumber)
  const routingNumberClone = formStrategy.showBankingNumbers && R.clone(defaultValues.routingNumber)

  useEffect(() => {
    formStrategy.showBankingNumbers && formControl.setValue('routingNumber', '')
    formStrategy.showBankingNumbers && formControl.setValue('accountNumber', '')
  },[formStrategy])

  return (
    <Body style={{ position: 'relative' }}>
      {isFetching && (
        <FetchingIndicator size="large" />
      )}

      <CJForm {...formControl} onSubmit={onFormSubmit}>
        <CJField name="name" label={intl.alternatePayeeForm.alternatePayeeNameLabel} required>
          <TextInput
            name="name"
            required
            maxLength={{
              value: 100,
              message: intl.alternatePayeeForm.errorMaxLength
            }}
            readOnly={formStrategy.isReadOnly}
          />
        </CJField>

        <CJField name="paymentMethodId" label={intl.alternatePayeeForm.bankCountryLabel} required>
          <SelectInput
            unpack
            name="paymentMethodId"
            required
            options={bankCountryOptions}
          />
        </CJField>

        <CJField name="paymentCurrency" label={intl.alternatePayeeForm.desiredPayoutCurrencyLabel} required>
          <SelectInput
            unpack
            name="paymentCurrency"
            required
            options={paymentCurrencyOptions}
          />
        </CJField>

        <CJField name="accountType" label={intl.alternatePayeeForm.accountTypeLabel} required>
          <SelectInput
            unpack
            name="accountType"
            required
            options={accountTypeOptions}
          />
        </CJField>

        <CJField name="routingNumber" label={intl.alternatePayeeForm.routingNumberLabel} required>
          {formStrategy.showBankingNumbers && routingNumberClone && (
            <Text size={12} color="subdued" style={{ marginTop: '-0.5rem' }}>
              {intl.alternatePayeeForm.currentRoutingNumberLabel}: {routingNumberClone}
            </Text>
          )}
          <TextInput
            name="routingNumber"
            required
            maxLength={{
              value: 40,
              message: intl.alternatePayeeForm.errorMaxLength
            }}
            validate={ (value: string) =>
              validator.isValidRoutingNumber(value, intl.alternatePayeeForm.errorRoutingNumber) }
            readOnly={formStrategy.isReadOnly}
          />
        </CJField>


        <CJField name="accountNumber" label={intl.alternatePayeeForm.accountNumberLabel} required>
          {formStrategy.showBankingNumbers && accountNumberClone && (
            <Text size={12} color="subdued" style={{ marginTop: '-0.5rem' }}>
              {intl.alternatePayeeForm.currentAccountNumberLabel}: {accountNumberClone}
            </Text>
          )}
          <TextInput
            name="accountNumber"
            required
            maxLength={{
              value: 40,
              message: intl.alternatePayeeForm.errorMaxLength
            }}
            readOnly={formStrategy.isReadOnly}
          />
        </CJField>

        <CJField name="payableTo" label={intl.alternatePayeeForm.accountHolderNameLabel} required>
          <TextInput
            name="payableTo"
            required
            maxLength={{
              value: 120,
              message: intl.alternatePayeeForm.errorMaxLength
            }}
            readOnly={formStrategy.isReadOnly}
          />
        </CJField>

        <CJField name="finInstitutionName" label={intl.alternatePayeeForm.bankNameLabel} required>
          <TextInput
            name="finInstitutionName"
            required
            maxLength={{
              value: 120,
              message: intl.alternatePayeeForm.errorMaxLength
            }}
            readOnly={formStrategy.isReadOnly}
          />
        </CJField>

        <LastModificationDate
          date={defaultValues.lastModified}
          dateLabel={intl.alternatePayeeForm.lastModifiedDateLabel}
          helperLabel={formStrategy.isReadOnly ? '' : intl.alternatePayeeForm.helperLabel}
        />

        <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
          {formStrategy.buttons.map((button, index) => (
            <Button
              key={index}
              type={button.type}
              htmlType={button.htmlType || 'button'}
              onClick={button.onClick}
              disabled={isFetching}
            >
              {getButtonLabel(button.labelKey)}
            </Button>
          ))}
        </div>
      </CJForm>
    </Body>
  )
}

export default AlternatePayeeForm

const LastModificationDate = ({ date, dateLabel, helperLabel }: LastModificationDateProps) => {
  const isHelperLabelEmpty = R.isEmpty(helperLabel)
  const isDateEmpty = R.isEmpty(date)
  return isDateEmpty ? <></> :
    <>
      <Notification border={false} type={'info'} padding={'small'}
                    iconSize={'1.3em'}
                    align={'center'}
                    gap={'small'}
      >
        <Text size={13} weight="medium">{dateLabel}</Text>
        <Text size={13} weight="regular" color="subdued">
          {date}
        </Text>
        {
          isHelperLabelEmpty ? <></> : (
            <Text size={13} weight="medium" color="subdued" italic>
              - ({helperLabel})
            </Text>
          )
        }

      </Notification>
    </>
}